import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import LoginPage from './LoginPage';
import SignUpPage from './SignUpPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import Dashboard from './Dashboard';
import LoadingSpinner from './ui/LoadingSpinner';

const AUTH_VIEWS = {
  LOGIN: 'login',
  SIGNUP: 'signup',
  FORGOT_PASSWORD: 'forgot_password',
  RESET_PASSWORD: 'reset_password',
};

export default function Locate2uPortal() {
  const { user, loading, isPasswordRecovery, clearPasswordRecovery, logout } = useAuth();
  const [authView, setAuthView] = useState(AUTH_VIEWS.LOGIN);

  // Detect /reset-password URL on mount to show reset password page
  useEffect(() => {
    if (window.location.pathname === '/reset-password') {
      setAuthView(AUTH_VIEWS.RESET_PASSWORD);
    }
  }, []);

  // Handle navigation back to login (clear URL, recovery mode, and session)
  const handleBackToLogin = async () => {
    window.history.replaceState({}, '', '/');
    clearPasswordRecovery();
    setAuthView(AUTH_VIEWS.LOGIN);
    await logout();
  };

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" className="mx-auto mb-4" />
          <p className="text-locate2-light-gray">Loading...</p>
        </div>
      </div>
    );
  }

  // Show reset password page if in password recovery mode (even if user is authenticated)
  // This happens when user clicks the reset link from email - Supabase creates a session
  if (isPasswordRecovery || (user && window.location.pathname === '/reset-password')) {
    return (
      <ResetPasswordPage
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  // Show dashboard if authenticated
  if (user) {
    return <Dashboard />;
  }

  // Show appropriate auth page based on current view
  switch (authView) {
    case AUTH_VIEWS.SIGNUP:
      return (
        <SignUpPage
          onSwitchToLogin={() => setAuthView(AUTH_VIEWS.LOGIN)}
        />
      );
    case AUTH_VIEWS.FORGOT_PASSWORD:
      return (
        <ForgotPasswordPage
          onBackToLogin={() => setAuthView(AUTH_VIEWS.LOGIN)}
        />
      );
    case AUTH_VIEWS.RESET_PASSWORD:
      return (
        <ResetPasswordPage
          onBackToLogin={handleBackToLogin}
        />
      );
    case AUTH_VIEWS.LOGIN:
    default:
      return (
        <LoginPage
          onSwitchToSignUp={() => setAuthView(AUTH_VIEWS.SIGNUP)}
          onForgotPassword={() => setAuthView(AUTH_VIEWS.FORGOT_PASSWORD)}
        />
      );
  }
}
